import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './Dashboard';
import Topics from './Topics';
import MCQ from './MCQ';
import Timetable from './Timetable';
import MCQScraper from './MCQScraper';
import Recall from './Recall';
import Feedback from './Feedback';
import Settings from './Settings';
import Changelog from './Changelog';
import ProgressChart from './ProgressChart';
import PracticeExams from './PracticeExams';
import SRAQuestions from './SRAQuestions';
import LawDrillsReview from './LawDrillsReview';
import ImportSummary from './ImportSummary';
import ImportHistory from './ImportHistory';
import Onboarding from './Onboarding';
import TourOverlay from './AppTour';
import { HelpImportBarbri, HelpImportLawDrills } from './HelpPages';
import { LATEST_CHANGELOG_DATE } from './Changelog';
import { Lightning, Close, ChevronUp, ChevronDown, Moon, Sun, SunWarm, Download, Logout, Gear } from './Icons';
import ROUTES from './shared/constants/routes';
import ProtectedRoute from './shared/components/ProtectedRoute';
import { api, trackEvent, downloadExport } from './shared/utils/api';
import './design-system.css';
import './styles.css';

// Re-export for any remaining legacy imports
export { api, trackEvent, downloadExport };

const API = process.env.REACT_APP_API_URL || '';

function AlertBanner({ alerts, onDismiss }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="alert-banner">
      {alerts.map(a => (
        <div key={a.id} className="alert-item">
          <span className="alert-icon"><Lightning size={14} /></span>
          <span className="alert-text">{a.message}</span>
          <button className="alert-dismiss" onClick={() => onDismiss(a.id)}><Close size={12} /></button>
        </div>
      ))}
    </div>
  );
}

// Feature flags — set to true to re-enable hidden tabs
const FEATURE_FLAGS = {
  mcqResults: false,    // Paste parser — hidden until BARBRI extension built
  practiceExams: false,  // Mock exam tracker — hidden until tested with real users
};

// Maps tab keys to route paths for nav and programmatic navigation
const TAB_ROUTES = {
  dashboard: ROUTES.DASHBOARD,
  flk1: ROUTES.FLK1,
  flk2: ROUTES.FLK2,
  recall: ROUTES.RECALL,
  timetable: ROUTES.PLANNER,
  mcq: ROUTES.MCQ,
  mcqresults: ROUTES.MCQ_SCRAPER,
  exams: ROUTES.PRACTICE,
  progress: ROUTES.PROGRESS,
  sra: ROUTES.SRA,
  lawdrills: ROUTES.LAWDRILLS,
  imports: '/import-history',
  feedback: ROUTES.FEEDBACK,
  changelog: ROUTES.CHANGELOG,
  settings: ROUTES.SETTINGS,
};

const NAV_GROUPS = [
  { key: 'overview',  label: 'Overview',  tabs: ['dashboard'] },
  { key: 'study',     label: 'Study',     tabs: ['flk1', 'flk2', 'recall'] },
  { key: 'practice',  label: 'Practice',  tabs: ['sra', 'mcq', ...(FEATURE_FLAGS.mcqResults ? ['mcqresults'] : []), ...(FEATURE_FLAGS.practiceExams ? ['exams'] : []), 'lawdrills'] },
  { key: 'planner',   label: 'Planner',   tabs: ['timetable'] },
  { key: 'progress',  label: 'Progress',  tabs: ['progress'] },
  { key: 'more',      label: 'More',      tabs: ['imports', 'feedback', 'changelog', 'settings'] },
];

const TAB_LABELS = {
  dashboard: 'Overview', flk1: 'FLK1', flk2: 'FLK2', recall: 'Recall Blurt',
  mcq: 'MCQ Log', mcqresults: 'MCQ Results', exams: 'Mock Exams',
  sra: 'SRA Questions',
  lawdrills: 'Law Drills',
  imports: 'Import history',
  timetable: 'Planner', progress: 'Progress',
  feedback: 'Feedback', changelog: 'What is new', settings: 'Settings',
};

function isTabActive(tabKey, pathname) {
  const route = TAB_ROUTES[tabKey];
  if (!route) return false;
  if (route === ROUTES.DASHBOARD) return pathname === '/app' || pathname === '/app/';
  return pathname === route || pathname.startsWith(route + '/');
}

function GroupedNav({ changelogNew, feedbackBadge }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const navRef = useRef(null);
  const buttonRefs = useRef({});

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenGroup(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleGroup = (key) => {
    if (openGroup === key) { setOpenGroup(null); return; }
    const btn = buttonRefs.current[key];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpenGroup(key);
  };

  const handleGroupKeyDown = (e, key) => {
    if (e.key === 'Escape') { setOpenGroup(null); buttonRefs.current[key]?.focus(); }
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(key); }
    if (e.key === 'ArrowDown' && openGroup === key) {
      e.preventDefault();
      const dropdown = navRef.current?.querySelector('.nav-dropdown');
      dropdown?.querySelector('.nav-dropdown-item')?.focus();
    }
  };

  const handleDropdownKeyDown = (e) => {
    const items = Array.from(navRef.current?.querySelectorAll('.nav-dropdown-item') || []);
    const idx = items.indexOf(e.target);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(idx + 1) % items.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus(); }
    if (e.key === 'Escape') { setOpenGroup(null); buttonRefs.current[openGroup]?.focus(); }
  };

  const goToTab = (t) => {
    if (t === 'imports') {
      window.location.href = '/import-history';
    } else {
      navigate(TAB_ROUTES[t]);
    }
    setOpenGroup(null);
  };

  return (
    <nav className="header-nav" ref={navRef}>
      {NAV_GROUPS.map(group => {
        const isActive = group.tabs.some(t => isTabActive(t, location.pathname));
        const isSingle = group.tabs.length === 1;
        const hasNew = group.key === 'more' && (changelogNew || feedbackBadge);

        if (isSingle) {
          return (
            <button key={group.key}
              className={`nav-btn ${isActive ? 'active' : ''}`}
              onClick={() => { goToTab(group.tabs[0]); }}>
              {group.label}
            </button>
          );
        }

        return (
          <div key={group.key} className="nav-group">
            <button
              ref={el => buttonRefs.current[group.key] = el}
              className={`nav-btn nav-group-btn ${isActive ? 'active' : ''} ${openGroup === group.key ? 'nav-group-open' : ''}`}
              onClick={() => toggleGroup(group.key)}
              onKeyDown={e => handleGroupKeyDown(e, group.key)}
              aria-expanded={openGroup === group.key}
              aria-haspopup="true">
              {group.label}
              {hasNew && !isActive && <span className="nav-new-dot" />}
              <span className="nav-group-arrow">{openGroup === group.key ? <ChevronUp size={10} /> : <ChevronDown size={10} />}</span>
            </button>
          </div>
        );
      })}
      {openGroup && (
        <div className="nav-dropdown" role="menu" style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          {NAV_GROUPS.find(g => g.key === openGroup)?.tabs.map(t => (
            <button key={t} role="menuitem"
              className={`nav-dropdown-item ${isTabActive(t, location.pathname) ? 'active' : ''}`}
              onClick={() => goToTab(t)}
              onKeyDown={handleDropdownKeyDown}>
              {TAB_LABELS[t]}
              {t === 'changelog' && changelogNew && !isTabActive('changelog', location.pathname) && <span className="nav-new-dot" style={{ marginLeft: 6 }} />}
              {t === 'feedback' && feedbackBadge && <span className="nav-new-dot" style={{ marginLeft: 6, background: 'var(--accent)' }} />}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

function MobileNav({ changelogNew, feedbackBadge, isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const goToTab = (t) => {
    if (t === 'imports') {
      window.location.href = '/import-history';
    } else {
      navigate(TAB_ROUTES[t]);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="mobile-nav-overlay" onClick={onClose} />
      <div className="mobile-nav-drawer">
        <div className="mobile-nav-header">
          <svg viewBox="0 0 32 32" width="18" height="18" aria-hidden="true">
            <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
            <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
            <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
          </svg>
          <span className="header-logo-text"><span className="header-logo-sqe">SQE</span><span className="header-logo-track">Track</span></span>
          <button className="mobile-nav-close" onClick={onClose} aria-label="Close menu"><Close size={16} /></button>
        </div>
        <nav className="mobile-nav-body">
          {NAV_GROUPS.map(group => (
            <div key={group.key} className="mobile-nav-group">
              <div className="mobile-nav-group-label">{group.label}</div>
              {group.tabs.map(t => (
                <button key={t}
                  className={`mobile-nav-item ${isTabActive(t, location.pathname) ? 'active' : ''}`}
                  onClick={() => goToTab(t)}>
                  {TAB_LABELS[t]}
                  {t === 'changelog' && changelogNew && <span className="nav-new-dot" style={{ marginLeft: 6 }} />}
                  {t === 'feedback' && feedbackBadge && <span className="nav-new-dot" style={{ marginLeft: 6, background: 'var(--accent)' }} />}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
}

// Defined at module level so its identity is stable — defining it inside AppRoutes
// would create a new function reference on every render, causing React to
// unmount/remount the entire shell tree (resetting Settings sub-tab state, etc.).
function AppShell({
  theme, onThemeToggle,
  changelogNew, feedbackBadge,
  mobileNavOpen, onOpenMobileNav, onCloseMobileNav,
  alerts, onDismissAlert,
  userName, userEmail, onLogout, onNavigateToSettings,
  onboardingDone, tourDone, onTourDone, onNavigateByTab,
  children,
}) {
  return (
    <div className="app">
      <header className="header">
        <button className="mobile-hamburger" onClick={onOpenMobileNav} aria-label="Open menu">
          <span /><span /><span />
        </button>
        <div className="header-title">
          <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true">
            <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
            <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
            <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
          </svg>
          <span className="header-logo-text"><span className="header-logo-sqe">SQE</span><span className="header-logo-track">Track</span></span>
        </div>
        <GroupedNav changelogNew={changelogNew} feedbackBadge={feedbackBadge} />
        <div className="header-actions">
          <button className="icon-btn" onClick={onThemeToggle}
            title={`Theme: ${theme === 'default' ? 'Default' : theme === 'warm' ? 'Warm' : 'Dark'} — click to cycle`}
            aria-label="Cycle display theme">
            {theme === 'dark' ? <Sun size={15} /> : theme === 'warm' ? <SunWarm size={15} /> : <Moon size={15} />}
          </button>
          <button className="icon-btn" onClick={onNavigateToSettings} title="Settings" aria-label="Settings">
            <Gear size={15} />
          </button>
          <button className="export-btn" onClick={downloadExport} aria-label="Export data"><Download size={13} /> Export</button>
          <button className="icon-btn" onClick={onLogout}
            title={`Sign out${userName ? ` (${userName})` : ` (${userEmail})`}`}
            aria-label="Log out">
            <Logout size={15} />
          </button>
        </div>
      </header>
      <MobileNav changelogNew={changelogNew} feedbackBadge={feedbackBadge} isOpen={mobileNavOpen} onClose={onCloseMobileNav} />
      <AlertBanner alerts={alerts} onDismiss={onDismissAlert} />
      <main className="main">
        {children}
      </main>
      {onboardingDone && !tourDone && (
        <TourOverlay setTab={onNavigateByTab} onDone={onTourDone} />
      )}
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'forgot-sent'

  const params = new URLSearchParams(window.location.search);
  const paymentStatus = params.get('payment');
  const redirectTarget = params.get('redirect') || ROUTES.DASHBOARD;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      let recaptcha_token = '';
      try {
        recaptcha_token = await new Promise(resolve => {
          window.grecaptcha.ready(async () => {
            try {
              const t = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '', { action: 'login' });
              resolve(t);
            } catch { resolve(''); }
          });
        });
      } catch (_) {}
      const res = await fetch(API + '/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, recaptcha_token }),
      }).then(r => r.json());

      if (res.ok && res.token) {
        localStorage.setItem('sqetrack_token', res.token);
        navigate(redirectTarget, { replace: true });
      } else {
        setError(res.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await fetch(API + '/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setView('forgot-sent');
    } catch (err) {
      setError('Network error. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (view === 'forgot') {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo">
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
              <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
              <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
              <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
            </svg>
            <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
          </div>
          <p className="login-sub">Reset your password</p>
          <form onSubmit={handleForgot}>
            <input
              className="login-input"
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              autoFocus
              required
            />
            {error && <p className="login-error">{error}</p>}
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? 'Sending' : 'Send reset link'}
            </button>
          </form>
          <p className="login-footer">
            <button className="login-link-btn" onClick={() => setView('login')}>Back to sign in</button>
          </p>
        </div>
      </div>
    );
  }

  if (view === 'forgot-sent') {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo">
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
              <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
              <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
              <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
            </svg>
            <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
          </div>
          <p className="login-sub">Check your email</p>
          <p style={{ fontSize: '.9rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            If an account exists for <strong style={{ color: 'var(--text2)' }}>{email}</strong>, you'll receive a password reset link within a few minutes.
          </p>
          <p className="login-footer">
            <button className="login-link-btn" onClick={() => setView('login')}>Back to sign in</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-box">
        <div className="login-logo">
          <svg viewBox="0 0 32 32" width="36" height="36" aria-hidden="true">
            <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
            <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
            <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
          </svg>
          <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
        </div>

        {paymentStatus === 'success' && (
          <div className="login-notice login-notice--success">
            Payment confirmed. Your account is being activated.
          </div>
        )}
        {paymentStatus === 'cancelled' && (
          <div className="login-notice login-notice--error">
            Payment was cancelled. You can try again from the <a href="/">home page</a>.
          </div>
        )}

        <form onSubmit={handleLogin}>
          <input
            className="login-input"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(''); }}
            autoFocus
            required
          />
          <input
            className="login-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            required
          />
          {error && <p className="login-error">{error}</p>}
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Signing in' : 'Sign in'}
          </button>
        </form>

        <p className="login-footer">
          <button className="login-link-btn" onClick={() => { setView('forgot'); setError(''); }}>
            Forgot password?
          </button>
        </p>
        <p className="login-footer">
          Don't have an account? <a href="/">Get access</a>
        </p>
      </div>
    </div>
  );
}

function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo">
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
              <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
              <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
              <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
            </svg>
            <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
          </div>
          <p className="login-error">This reset link is invalid or has expired. Request a new one from the login page.</p>
          <p className="login-footer"><a href="/login">Back to sign in</a></p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-wrap">
        <div className="login-box">
          <div className="login-logo">
            <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
              <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)"/>
              <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)"/>
              <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)"/>
            </svg>
            <span className="login-logo-text"><span className="login-logo-sqe">SQE</span><span className="login-logo-track">Track</span></span>
          </div>
          <div className="login-notice login-notice--success">Password updated. Sign in with your new password.</div>
          <p className="login-footer"><a href="/login">Sign in</a></p>
        </div>
      </div>
    );
  }

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(API + '/api/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      }).then(r => r.json());
      if (res.ok) { setDone(true); }
      else { setError(res.error || 'Password reset failed. The link may have expired. Request a new one from the login page.'); }
    } catch (err) {
      setError('Network error. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-box">
        <div className="login-logo">SQE<span className="header-title-accent">Track</span></div>
        <p className="login-sub">Set a new password</p>
        <form onSubmit={handleReset}>
          <input className="login-input" type="password" placeholder="New password (8+ characters)"
            value={password} onChange={e => { setPassword(e.target.value); setError(''); }} autoFocus required />
          <input className="login-input" type="password" placeholder="Confirm new password"
            value={confirm} onChange={e => { setConfirm(e.target.value); setError(''); }} required />
          {error && <p className="login-error">{error}</p>}
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Updating' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Inner app — rendered inside BrowserRouter so useNavigate/useLocation work
function AppRoutes() {
  const navigate = useNavigate();
  const [focusTopicId, setFocusTopicId] = useState(null);
  const [sraInitialSubject, setSraInitialSubject] = useState(null);
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('sqe_theme');
    if (stored === 'warm' || stored === 'dark') return stored;
    return 'default';
  });
  const [alerts, setAlerts] = useState([]);
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [onboardingDone, setOnboardingDone] = useState(true);
  const [tourDone, setTourDone] = useState(true);
  const [changelogNew, setChangelogNew] = useState(false);
  const [feedbackBadge, setFeedbackBadge] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    document.body.classList.remove('dark', 'warm');
    if (theme === 'dark') document.body.classList.add('dark');
    if (theme === 'warm') document.body.classList.add('warm');
    localStorage.setItem('sqe_theme', theme);
  }, [theme]);

  // Check if JWT is valid on mount
  useEffect(() => {
    const token = localStorage.getItem('sqetrack_token');
    if (!token) {
      setChecking(false);
      return;
    }
    fetch(API + '/api/me', {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.id) {
          setAuthed(true);
          setUserEmail(data.email || '');
          setUserName(data.name || '');
          fetch(API + '/api/settings', {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          }).then(r => r.json()).then(s => {
            setOnboardingDone(s.onboarding_complete !== '0');
            setTourDone(s.tour_shown !== '0');
            const lastViewed = s.changelog_last_viewed || '';
            setChangelogNew(LATEST_CHANGELOG_DATE > lastViewed);
            if (s.display_theme === 'warm' || s.display_theme === 'dark' || s.display_theme === 'default') {
              setTheme(s.display_theme);
            }
          }).catch((err) => { console.error('[App] Failed to load user settings:', err); });
          fetch(API + '/api/feedback/has-unseen', {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          }).then(r => r.json()).then(d => {
            setFeedbackBadge(d.has_unseen || false);
          }).catch((err) => { console.error('[App] Failed to load feedback badge:', err); });
        } else {
          localStorage.removeItem('sqetrack_token');
        }
      })
      .catch(() => {
        localStorage.removeItem('sqetrack_token');
      })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (authed) {
      api('/api/alerts').then(setAlerts).catch((err) => { console.error('[App] Failed to load alerts:', err); });
      const interval = setInterval(() => api('/api/alerts').then(setAlerts).catch((err) => { console.error('[App] Alert poll failed:', err); }), 60000);
      return () => clearInterval(interval);
    }
  }, [authed]);

  // Handle payment=success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      const token = localStorage.getItem('sqetrack_token');
      if (token) {
        navigate(ROUTES.DASHBOARD, { replace: true });
      }
    }
  }, []); // eslint-disable-line

  const dismissAlert = async (id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
    await api('/api/alerts/dismiss', { method: 'POST', body: JSON.stringify({ id }) });
  };

  const navigateToTopic = useCallback((topicId, module) => {
    setFocusTopicId(topicId);
    navigate(module === 'FLK1' ? ROUTES.FLK1 : ROUTES.FLK2);
  }, [navigate]);

  const navigateToSRA = useCallback((subjectArea) => {
    setSraInitialSubject(subjectArea || null);
    navigate(ROUTES.SRA);
  }, [navigate]);

  const resetTooltips = useCallback(async () => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({ tour_shown: '0' }) });
    setTourDone(false);
  }, []);

  const logout = () => {
    localStorage.removeItem('sqetrack_token');
    // Hard redirect: avoids ProtectedRoute racing to add ?redirect=, and
    // kills all in-flight component renders that rely on the now-removed token.
    window.location.href = '/login';
  };

  // Navigate by tab key — used by TourOverlay and Onboarding
  const navigateByTab = useCallback((tabKey) => {
    if (tabKey && TAB_ROUTES[tabKey]) {
      navigate(TAB_ROUTES[tabKey]);
    }
  }, [navigate]);

  if (checking) {
    return <div className="login-wrap"><div className="login-box"><p>Loading</p></div></div>;
  }

  if (!onboardingDone && authed) {
    return (
      <Onboarding
        userName={userName}
        onComplete={(name, firstTab) => {
          setUserName(name);
          setOnboardingDone(true);
          if (firstTab && TAB_ROUTES[firstTab]) navigate(TAB_ROUTES[firstTab]);
        }}
      />
    );
  }

  return (
    <Routes>
      <Route path={ROUTES.LOGIN} element={
        authed ? <Navigate to={ROUTES.DASHBOARD} replace /> : <LoginPage />
      } />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route path="/help/import-barbri" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      <Route path="/help/import-law-drills" element={<HelpImportLawDrills />} />
      <Route path="/import-summary" element={<ImportSummary />} />
      <Route path="/import-history" element={<ImportHistory />} />
      <Route path="/app/*" element={
        <ProtectedRoute>
          <AppShell
            theme={theme}
            onThemeToggle={() => {
              const next = theme === 'default' ? 'warm' : theme === 'warm' ? 'dark' : 'default';
              setTheme(next);
              api('/api/settings', { method: 'POST', body: JSON.stringify({ display_theme: next }) }).catch(() => {});
            }}
            changelogNew={changelogNew}
            feedbackBadge={feedbackBadge}
            mobileNavOpen={mobileNavOpen}
            onOpenMobileNav={() => setMobileNavOpen(true)}
            onCloseMobileNav={closeMobileNav}
            alerts={alerts}
            onDismissAlert={dismissAlert}
            userName={userName}
            userEmail={userEmail}
            onLogout={logout}
            onNavigateToSettings={() => navigate(ROUTES.SETTINGS)}
            onboardingDone={onboardingDone}
            tourDone={tourDone}
            onTourDone={() => setTourDone(true)}
            onNavigateByTab={navigateByTab}
          >
            <Routes>
              <Route path="/" element={<Dashboard onTabChange={navigateByTab} />} />
              <Route path="/topics/flk1" element={<Topics module="FLK1" focusTopicId={focusTopicId} onFocusConsumed={() => setFocusTopicId(null)} tourHighlight={!tourDone} onNavigateToSRA={navigateToSRA} />} />
              <Route path="/topics/flk2" element={<Topics module="FLK2" focusTopicId={focusTopicId} onFocusConsumed={() => setFocusTopicId(null)} onNavigateToSRA={navigateToSRA} />} />
              <Route path="/planner" element={<Timetable onNavigateToTopic={navigateToTopic} />} />
              <Route path="/progress" element={<ProgressChart />} />
              <Route path="/settings" element={<Settings onRestartTour={resetTooltips} theme={theme} onThemeChange={(t) => {
                setTheme(t);
                api('/api/settings', { method: 'POST', body: JSON.stringify({ display_theme: t }) }).catch(() => {});
              }} />} />
              <Route path="/sra" element={<SRAQuestions initialSubject={sraInitialSubject} onInitialSubjectConsumed={() => setSraInitialSubject(null)} />} />
              <Route path="/mcq" element={<MCQ />} />
              <Route path="/mcq/import" element={<MCQScraper />} />
              <Route path="/recall" element={<Recall />} />
              <Route path="/feedback" element={<Feedback onViewed={() => {
                setFeedbackBadge(false);
                api('/api/settings', { method: 'POST', body: JSON.stringify({ last_feedback_viewed: new Date().toISOString() }) });
              }} />} />
              <Route path="/practice" element={<PracticeExams />} />
              <Route path="/changelog" element={<Changelog onViewed={() => setChangelogNew(false)} />} />
              <Route path="/lawdrills" element={<LawDrillsReview />} />
              <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
            </Routes>
          </AppShell>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
